import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseHoaDonDieuChinhGiaHangBanReturn {
  hoaDonDieuChinhGiaHangBans: any[];
  isLoading: boolean;
  refreshHoaDonDieuChinhGiaHangBans: () => Promise<void>;
  filterHoaDonDieuChinhGiaHangBans: (filters: Record<string, any>) => Promise<any[]>;
}

/**
 * Hook for managing Hoa Don Dieu Chinh Gia Hang Ban (Price Adjustment Invoice) data
 *
 * This hook provides functions to fetch and filter price adjustment invoices.
 */
export const useHoaDonDieuChinhGiaHangBan = (initialData: any[] = []): UseHoaDonDieuChinhGiaHangBanReturn => {
  const [hoaDonDieuChinhGiaHangBans, setHoaDonDieuChinhGiaHangBans] = useState<any[]>(initialData);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  /**
   * Fetch all price adjustment invoices
   */
  const fetchHoaDonDieuChinhGiaHangBans = useCallback(async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_DIEU_CHINH_GIA_HANG_BAN}`);
      setHoaDonDieuChinhGiaHangBans(response.data.results || []);
    } catch (error) {
      console.error('Error fetching price adjustment invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  /**
   * Filter price adjustment invoices using the filter API
   */
  const filterHoaDonDieuChinhGiaHangBans = useCallback(
    async (filters: Record<string, any>): Promise<any[]> => {
      if (!entity?.slug) return [];

      try {
        const response = await api.post(
          `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-gia-hang-ban/filter/`,
          filters
        );

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (response.data?.results) {
          return response.data.results;
        }

        return [];
      } catch (error) {
        console.error('Error filtering price adjustment invoices via filter API:', error);
        return [];
      }
    },
    [entity?.slug]
  );

  // Fetch data on component mount or when entity changes
  useEffect(() => {
    fetchHoaDonDieuChinhGiaHangBans();
  }, [entity?.slug]);

  // Create a stable reference for refreshHoaDonDieuChinhGiaHangBans
  const refreshHoaDonDieuChinhGiaHangBans = useCallback(() => {
    return fetchHoaDonDieuChinhGiaHangBans();
  }, [fetchHoaDonDieuChinhGiaHangBans]);

  return {
    hoaDonDieuChinhGiaHangBans,
    isLoading,
    refreshHoaDonDieuChinhGiaHangBans,
    filterHoaDonDieuChinhGiaHangBans
  };
};
